/**
 * The filter lives in the query string, so any view is a link you can paste in a
 * message. Both screens read the same state through `useFilter`, which is why
 * switching screens keeps the selection.
 *
 * Parsing is deliberately tolerant per key: one hand-edited parameter should not
 * throw away the rest of the filter.
 */
import {
  INT4_MAX,
  INT4_MIN,
  LISTING_SORTERINGEN,
  type ListingSortering,
  listingFilterCriteriaSchema,
} from '@mamimap/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { navigeer, usePad, useZoekParams } from './router';

/**
 * `zonderCoordinaat` is het enige criterium dat de API niet kent: de kaart toont
 * per definitie alleen woningen mét punt, dus er is geen serverfilter voor de
 * andere kant. Het scherm doet het daarom zelf - net als de sortering op label -
 * maar het hoort wél in de URL, want het is een selectie die je deelt.
 */
const filterSchema = listingFilterCriteriaSchema
  .omit({ sortering: true, bbox: true })
  .extend({ zonderCoordinaat: z.stringbool().optional() });

/** Criteria die de API niet kent en die het scherm zelf toepast. */
export const EIGEN_CRITERIA = ['zonderCoordinaat'] as const;

/**
 * Het filter zoals de API het kent. Alleen hierop wordt gecached en bevraagd:
 * anders haalt een klik op 'zonder coordinaat' dezelfde 928 punten nog een keer
 * op onder een nieuwe sleutel.
 */
export function serverFilter(filter: Filter): ServerFilter {
  const { zonderCoordinaat: _eigen, ...rest } = filter;
  return rest;
}

export type Filter = z.infer<typeof filterSchema>;
export type ServerFilter = Omit<Filter, (typeof EIGEN_CRITERIA)[number]>;
export type FilterSleutel = keyof Filter;

/**
 * Sorteren op energielabel is het enige wat de database niet aanbiedt: A t/m G
 * is geen alfabetische of numerieke volgorde, dus die rijen worden hier geordend
 * met de comparator uit @mamimap/shared. Adres en postcode gingen hier ooit ook
 * doorheen - de tabel haalde dan stil de hele set op en sorteerde boven 2000
 * rijen nog maar een deel. Die twee kent `LISTING_SORTERINGEN` gewoon, inclusief
 * cursor-paginatie, en lopen sindsdien over de server.
 */
export const LOKALE_SORTERINGEN = ['label_op', 'label_af'] as const;
export type LokaleSortering = (typeof LOKALE_SORTERINGEN)[number];
export type Sortering = ListingSortering | LokaleSortering;

export const SORTERING_STANDAARD: Sortering = 'prijs_per_m2_af';

export function isLokaleSortering(sortering: Sortering): sortering is LokaleSortering {
  return (LOKALE_SORTERINGEN as readonly string[]).includes(sortering);
}

function leesSortering(waarde: string | null): Sortering {
  if (!waarde) return SORTERING_STANDAARD;
  if ((LISTING_SORTERINGEN as readonly string[]).includes(waarde)) return waarde as Sortering;
  if ((LOKALE_SORTERINGEN as readonly string[]).includes(waarde)) return waarde as Sortering;
  return SORTERING_STANDAARD;
}

/**
 * De database telt in 32-bits gehele getallen, en het contract in
 * @mamimap/shared weigert daarbuiten. `?prijsMin=99999999999` is dus geen filter
 * maar een typefout - die melden we met zoveel woorden in plaats van met het
 * algemene "kon niet gelezen worden".
 */
const GETALSLEUTELS = ['prijsMin', 'prijsMax', 'm2Min', 'm2Max', 'kamersMin', 'kamersMax'] as const;

/**
 * De plek die op de laag Buurten en cijfers gekozen is (MA-55): een BBGA-code
 * van een gebied, wijk of buurt, bijvoorbeeld `?plek=KE04`. Geen filter - de
 * woningen blijven dezelfde - maar wel een plek waar een link of een herlaadbeurt
 * op hoort uit te komen. Bewust niet `buurt`: dat lijkt op het filter `buurten`
 * (postcodes), en een keuze kan net zo goed een gebied of wijk zijn.
 */
export const PLEK_PARAMETER = 'plek';

/** Parameters die geen filter zijn maar wel in de URL horen. */
/* `woning` is de link naar één woning op de kaart, met zijn popupje open (MA-59). */
const OVERIGE_PARAMETERS = ['sortering', 'woning', PLEK_PARAMETER] as const;

/** Een BBGA-code: letters en cijfers, kort. Iets anders is geen plek. */
const PLEKCODE = /^[A-Za-z0-9]{1,12}$/;

/** De gekozen plek uit de querystring, of null. */
export function leesPlek(zoek: URLSearchParams): string | null {
  const ruw = zoek.get(PLEK_PARAMETER);
  return ruw && PLEKCODE.test(ruw) ? ruw : null;
}

/**
 * Dezelfde querystring met een andere plek (of zonder, bij null); de rest - het
 * filter, de sortering - blijft precies staan. Geeft de querystring zonder `?`.
 */
export function metPlek(zoek: string, plek: string | null): string {
  const nieuw = new URLSearchParams(zoek);
  if (plek && PLEKCODE.test(plek)) nieuw.set(PLEK_PARAMETER, plek);
  else nieuw.delete(PLEK_PARAMETER);
  return nieuw.toString();
}

export type Genegeerd = {
  parameter: string;
  reden: 'onbekend' | 'ongeldig' | 'te_groot';
  /** De naam die bedoeld leek, als we die met redelijke zekerheid kunnen noemen. */
  bedoeld?: string;
};

export type GelezenFilter = { filter: Filter; genegeerd: Genegeerd[] };

function afstand(a: string, b: string): number {
  const rij = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let vorige = rij[0] ?? 0;
    rij[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const bewaar = rij[j] ?? 0;
      rij[j] = Math.min(
        (rij[j] ?? 0) + 1,
        (rij[j - 1] ?? 0) + 1,
        vorige + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      vorige = bewaar;
    }
  }
  return rij[b.length] ?? 0;
}

/**
 * Welke bestaande parameter er bedoeld leek: `zoek` -> `zoekterm`,
 * `label` -> `energielabels` of `zonderLabel`. Zijn er twee even goede
 * kandidaten, dan noemen we ze allebei in plaats van er een te kiezen.
 */
export function bedoeldeParameters(parameter: string): string[] {
  const bekend = [...Object.keys(filterSchema.shape), ...OVERIGE_PARAMETERS].sort();
  const klein = parameter.toLowerCase();
  const gelijk = bekend.filter((naam) => naam.toLowerCase() === klein);
  if (gelijk.length > 0) return gelijk;
  const bevat = bekend.filter(
    (naam) => naam.toLowerCase().includes(klein) || klein.includes(naam.toLowerCase()),
  );
  if (bevat.length > 0) return bevat.slice(0, 2);
  const dichtbij = bekend
    .map((naam) => ({ naam, weg: afstand(klein, naam.toLowerCase()) }))
    .filter((kandidaat) => kandidaat.weg <= 2)
    .sort((a, b) => a.weg - b.weg);
  return dichtbij[0] ? [dichtbij[0].naam] : [];
}

export function bedoeldeParameter(parameter: string): string | undefined {
  return bedoeldeParameters(parameter)[0];
}

/**
 * Leest het filter uit de querystring én houdt bij wat er niet gelezen kon
 * worden. Dat tweede is het punt: de API geeft op een onbekende parameter een
 * 400 die de bedoelde naam noemt, en het scherm hoorde dat niet stilzwijgend
 * beter te weten - `?zoek=jordaan` filterde niets en niets verried dat.
 */
export function leesFilterMetMeldingen(zoek: URLSearchParams): GelezenFilter {
  const filter: Record<string, unknown> = {};
  const genegeerd: Genegeerd[] = [];

  for (const [sleutel, veld] of Object.entries(filterSchema.shape)) {
    const ruw = zoek.get(sleutel);
    if (ruw === null || ruw === '') continue;
    const uitkomst = (veld as z.ZodType).safeParse(ruw);
    if (!uitkomst.success || uitkomst.data === undefined) {
      const getal = Number(ruw);
      const buitenBereik =
        (GETALSLEUTELS as readonly string[]).includes(sleutel) &&
        Number.isFinite(getal) &&
        (getal > INT4_MAX || getal < INT4_MIN);
      genegeerd.push({ parameter: sleutel, reden: buitenBereik ? 'te_groot' : 'ongeldig' });
      continue;
    }
    filter[sleutel] = uitkomst.data;
  }

  const bekend = new Set<string>([...Object.keys(filterSchema.shape), ...OVERIGE_PARAMETERS]);
  for (const sleutel of new Set(zoek.keys())) {
    if (bekend.has(sleutel)) continue;
    if ((zoek.get(sleutel) ?? '') === '') continue;
    const kandidaten = bedoeldeParameters(sleutel);
    genegeerd.push({
      parameter: sleutel,
      reden: 'onbekend',
      bedoeld: kandidaten.length > 0 ? kandidaten.join(' of ') : undefined,
    });
  }

  return { filter: filter as Filter, genegeerd };
}

export function leesFilter(zoek: URLSearchParams): Filter {
  return leesFilterMetMeldingen(zoek).filter;
}

/** Eén regel die zegt wat er met de URL is gebeurd, in gewoon Nederlands. */
export function meldingOverGenegeerd(genegeerd: readonly Genegeerd[]): string | null {
  if (genegeerd.length === 0) return null;
  const zinnen = genegeerd.map((item) => {
    if (item.reden === 'te_groot') {
      return `${item.parameter} valt buiten het bereik en is genegeerd`;
    }
    if (item.reden === 'ongeldig') {
      return `${item.parameter} kon niet gelezen worden en is genegeerd`;
    }
    return item.bedoeld
      ? `${item.parameter} bestaat niet - bedoelde je ${item.bedoeld}?`
      : `${item.parameter} bestaat niet en is genegeerd`;
  });
  return zinnen.join(' · ');
}

/** Een woning-id in de link: letters, cijfers en streepjes (een uuid). */
const WONINGID = /^[A-Za-z0-9-]{1,64}$/;

/** De woning uit de link `?woning=<id>` (MA-59), of null. */
export function leesWoning(zoek: URLSearchParams): string | null {
  const ruw = zoek.get('woning');
  return ruw && WONINGID.test(ruw) ? ruw : null;
}

/**
 * Het filter als querystring; een gekozen plek en de woning uit de link reizen
 * ongewijzigd mee. Zonder die woning haalde de opschoning hieronder `?woning=`
 * meteen weg, nog voordat de kaart hem had kunnen openen.
 */
export function schrijfFilter(
  filter: Filter,
  sortering: Sortering,
  plek: string | null = null,
  woning: string | null = null,
): URLSearchParams {
  const zoek = new URLSearchParams();
  for (const [sleutel, waarde] of Object.entries(filter)) {
    if (waarde === undefined || waarde === null || waarde === '') continue;
    if (Array.isArray(waarde)) {
      if (waarde.length > 0) zoek.set(sleutel, waarde.join(','));
    } else {
      zoek.set(sleutel, String(waarde));
    }
  }
  if (sortering !== SORTERING_STANDAARD) zoek.set('sortering', sortering);
  if (plek) zoek.set(PLEK_PARAMETER, plek);
  if (woning && WONINGID.test(woning)) zoek.set('woning', woning);
  return zoek;
}

/** How many criteria are narrowing the result down right now. */
export function aantalActieveFilters(filter: Filter): number {
  return Object.values(filter).filter((waarde) =>
    Array.isArray(waarde) ? waarde.length > 0 : waarde !== undefined,
  ).length;
}

export type FilterState = {
  filter: Filter;
  sortering: Sortering;
  zet: (wijziging: Partial<Filter>) => void;
  zetSortering: (sortering: Sortering) => void;
  wis: () => void;
  aantalActief: number;
  /** Wat er uit de URL is weggevallen, en waarom. Leeg zodra de melding weg is. */
  genegeerd: readonly Genegeerd[];
  sluitMelding: () => void;
};

export function useFilter(): FilterState {
  const pad = usePad();
  const zoek = useZoekParams();
  const sleutel = zoek.toString();

  const gelezen = useMemo(() => leesFilterMetMeldingen(new URLSearchParams(sleutel)), [sleutel]);
  const filter = gelezen.filter;
  const sortering = useMemo(
    () => leesSortering(new URLSearchParams(sleutel).get('sortering')),
    [sleutel],
  );
  /** De gekozen plek op de laag Buurten en cijfers: blijft staan als het filter verandert. */
  const plek = useMemo(() => leesPlek(new URLSearchParams(sleutel)), [sleutel]);
  /** De woning uit de link (MA-59): blijft ook staan, tot iemand een andere opent. */
  const woning = useMemo(() => leesWoning(new URLSearchParams(sleutel)), [sleutel]);

  /**
   * Een filter aanzetten is een stap, geen zijstap: de terugknop hoort hem terug
   * te draaien. Daarom een push in plaats van een replace. Alleen een andere
   * sortering vervangt de huidige stap - dezelfde selectie, andere volgorde -
   * anders kost een kolomkop drie keer klikken ook drie keer terug.
   */
  const ga = useCallback(
    (nieuw: Filter, nieuweSortering: Sortering, vervang = false) => {
      const query = schrijfFilter(nieuw, nieuweSortering, plek, woning).toString();
      navigeer(query ? `${pad}?${query}` : pad, { vervang });
    },
    [pad, plek, woning],
  );

  /**
   * De opschoning hieronder haalt een onleesbare parameter meteen uit de URL, dus
   * wat er stond is één tel later niet meer te zien. Daarom onthouden we de
   * melding apart, tot de gebruiker hem wegklikt of zelf iets aan het filter doet.
   */
  const [genegeerd, zetGenegeerd] = useState<readonly Genegeerd[]>(gelezen.genegeerd);
  const sluitMelding = useCallback(() => zetGenegeerd([]), []);
  const meldsleutel = gelezen.genegeerd.map((item) => `${item.parameter}:${item.reden}`).join('|');
  // biome-ignore lint/correctness/useExhaustiveDependencies: `meldsleutel` is de waarde die telt; de lijst zelf is elke ronde een nieuw object.
  useEffect(() => {
    if (meldsleutel === '') return;
    zetGenegeerd(gelezen.genegeerd);
  }, [meldsleutel]);

  const zet = useCallback(
    (wijziging: Partial<Filter>) => {
      zetGenegeerd([]);
      const samen = { ...filter, ...wijziging } as Record<string, unknown>;
      for (const [k, v] of Object.entries(samen)) {
        if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
          delete samen[k];
        }
      }
      ga(samen as Filter, sortering);
    },
    [filter, ga, sortering],
  );

  const zetSortering = useCallback((nieuwe: Sortering) => ga(filter, nieuwe, true), [filter, ga]);

  const wis = useCallback(() => {
    zetGenegeerd([]);
    ga({}, sortering);
  }, [ga, sortering]);

  /**
   * Wat we niet konden lezen, laten we ook niet staan: `?prijsMin=abc` of een
   * parameter die niet bestaat filtert niets, maar reist wel mee in een gedeelde
   * link en suggereert daar iets dat er niet is. De URL wordt daarom eenmalig
   * teruggeschreven naar wat er werkelijk geldt - met een replace, want dit is
   * geen stap van de gebruiker.
   */
  useEffect(() => {
    const opgeschoond = schrijfFilter(filter, sortering, plek, woning).toString();
    if (opgeschoond === sleutel) return;
    navigeer(opgeschoond ? `${pad}?${opgeschoond}` : pad, { vervang: true });
  }, [filter, sortering, plek, woning, sleutel, pad]);

  return {
    filter,
    sortering,
    zet,
    zetSortering,
    wis,
    aantalActief: aantalActieveFilters(filter),
    genegeerd,
    sluitMelding,
  };
}
